<template>
  <div class="player-nutrition-list">
    <!-- Header with search and filters -->
    <div class="list-header">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search players..."
          class="search-input"
        />
      </div>
      <div class="filter-row">
        <select v-if="teams.length > 1" v-model="selectedTeam" class="team-filter">
          <option value="">All Teams</option>
          <option v-for="team in teams" :key="team.id" :value="team.id">
            {{ team.name }}
          </option>
        </select>
        <select v-model="statusFilter" class="status-filter">
          <option value="">All Status</option>
          <option value="good">On Track</option>
          <option value="moderate">Needs Attention</option>
          <option value="low">Behind</option>
          <option value="not_logged">Not Logged</option>
        </select>
      </div>
    </div>

    <!-- Summary stats -->
    <div class="summary-cards">
      <div class="summary-card">
        <span class="card-value">{{ players.length }}</span>
        <span class="card-label">Total Players</span>
      </div>
      <div class="summary-card logged">
        <span class="card-value">{{ loggedTodayCount }}</span>
        <span class="card-label">Logged Today</span>
      </div>
      <div class="summary-card attention">
        <span class="card-value">{{ needsAttentionCount }}</span>
        <span class="card-label">Needs Attention</span>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <span>Loading player data...</span>
    </div>

    <!-- Player list grouped by team -->
    <div v-else-if="filteredPlayers.length > 0" class="team-groups">
      <div
        v-for="team in groupedTeams"
        :key="team.id"
        class="team-group"
      >
        <button
          type="button"
          class="team-header"
          :aria-expanded="!isCollapsed(team.id)"
          @click="toggleTeam(team.id)"
        >
          <svg
            class="team-chevron"
            :class="{ collapsed: isCollapsed(team.id) }"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
          <span class="team-header-name">{{ team.name }}</span>
          <span class="team-header-meta">
            <span class="team-logged">{{ team.loggedCount }}/{{ team.players.length }} logged</span>
            <span class="team-count">{{ team.players.length }}</span>
          </span>
        </button>

        <div v-show="!isCollapsed(team.id)" class="player-grid">
          <div
            v-for="player in team.players"
            :key="player.id"
            class="player-card"
            @click="selectPlayer(player)"
          >
            <div class="player-avatar">
              <img
                v-if="player.image"
                :src="imgUrl(player.image)"
                :alt="player.name"
                class="avatar-img"
              />
              <div v-else class="avatar-placeholder">
                {{ getInitials(player.name) }}
              </div>
            </div>
            <div class="player-info">
              <h4 class="player-name">{{ player.name }}</h4>
            </div>
            <div class="nutrition-stats">
              <div class="stat-row">
                <span class="stat-icon hydration">H</span>
                <div class="stat-bar">
                  <div
                    class="stat-fill hydration"
                    :style="{ width: Math.min(100, player.hydration_pct || 0) + '%' }"
                  ></div>
                </div>
                <span class="stat-value">{{ player.hydration_pct || 0 }}%</span>
              </div>
              <div class="stat-row">
                <span class="stat-icon meals">M</span>
                <span class="meals-count">{{ player.meals_logged || 0 }} meals</span>
              </div>
            </div>
            <div class="status-badge" :style="getStatusStyle(player)">
              {{ getStatusLabel(player) }}
            </div>
            <div class="card-arrow">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <div class="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="9" cy="7" r="4"></circle>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
        </svg>
      </div>
      <h3>No Players Found</h3>
      <p v-if="searchQuery || statusFilter">
        No players match your current filters. Try adjusting your search or filters.
      </p>
      <p v-else>
        No players are assigned to you yet.
      </p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { Nutrition } from "@/services/nutrition-service";

export default {
  name: "PlayerNutritionList",

  props: {
    date: {
      type: String,
      default: () => new Date().toISOString().split("T")[0],
    },
  },

  data() {
    return {
      loading: true,
      players: [],
      teams: [],
      searchQuery: "",
      selectedTeam: "",
      statusFilter: "",
      collapsedTeams: {},
    };
  },

  computed: {
    ...mapGetters(["getProfile"]),

    isAdmin() {
      return this.getProfile?.userType === "Admin";
    },

    isCoach() {
      const userType = this.getProfile?.userType;
      const position = this.getProfile?.position;
      return userType === "Coach" || position === "Coach" || position === "HeadCoach";
    },

    clubId() {
      return this.getProfile?.club_id || this.getProfile?.clubId;
    },

    filteredPlayers() {
      let filtered = [...this.players];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(
          (p) =>
            p.name?.toLowerCase().includes(query) ||
            p.team_name?.toLowerCase().includes(query)
        );
      }

      // Team filter
      if (this.selectedTeam) {
        filtered = filtered.filter((p) => p.team_id === this.selectedTeam);
      }

      // Status filter
      if (this.statusFilter) {
        filtered = filtered.filter((p) => {
          const status = Nutrition.getPlayerNutritionStatus(p);
          return status.status === this.statusFilter;
        });
      }

      return filtered;
    },

    groupedTeams() {
      const groups = new Map();
      this.filteredPlayers.forEach((player) => {
        const key = player.team_id || "__no_team__";
        if (!groups.has(key)) {
          groups.set(key, {
            id: key,
            name: player.team_name || "Unassigned",
            players: [],
          });
        }
        groups.get(key).players.push(player);
      });

      return Array.from(groups.values())
        .map((group) => ({
          ...group,
          loggedCount: group.players.filter(
            (p) => (p.meals_logged || 0) > 0 || (p.hydration_pct || 0) > 0
          ).length,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },

    loggedTodayCount() {
      return this.players.filter((p) => (p.meals_logged || 0) > 0 || (p.hydration_pct || 0) > 0).length;
    },

    needsAttentionCount() {
      return this.players.filter((p) => {
        const status = Nutrition.getPlayerNutritionStatus(p);
        return status.status === "moderate" || status.status === "low";
      }).length;
    },
  },

  watch: {
    date() {
      this.loadPlayers();
    },
  },

  mounted() {
    this.loadPlayers();
  },

  methods: {
    async loadPlayers() {
      this.loading = true;
      try {
        const clubIdValue = this.isAdmin ? this.clubId : null;
        const coachId = this.isCoach ? this.getProfile?.id : null;
        const userType = this.isCoach ? "Coach" : "Admin";

        console.log("Loading players with:", { clubIdValue, coachId, userType, date: this.date });

        const response = await Nutrition.getTeamNutritionOverview(
          clubIdValue,
          coachId,
          this.date,
          userType
        );

        console.log("API response:", response.data);

        if (response.data?.success) {
          // Map the response data to expected format
          const rawPlayers = response.data.data || [];
          console.log("Raw players count:", rawPlayers.length);

          this.players = rawPlayers.map(p => ({
            ...p,
            team_id: p.teamId,
            team_name: p.teamName,
          }));

          // Extract unique teams for filter dropdown
          const teamMap = new Map();
          this.players.forEach((p) => {
            if (p.team_id && p.team_name && !teamMap.has(p.team_id)) {
              teamMap.set(p.team_id, { id: p.team_id, name: p.team_name });
            }
          });
          this.teams = Array.from(teamMap.values());

          // Default every team group to collapsed
          const collapsed = {};
          this.players.forEach((p) => {
            const key = p.team_id || "__no_team__";
            collapsed[key] = true;
          });
          this.collapsedTeams = collapsed;
        } else {
          console.error("API error:", response.data?.error);
        }
      } catch (error) {
        console.error("Error loading players:", error);
      } finally {
        this.loading = false;
      }
    },

    isCollapsed(teamId) {
      return !!this.collapsedTeams[teamId];
    },

    toggleTeam(teamId) {
      this.collapsedTeams = {
        ...this.collapsedTeams,
        [teamId]: !this.collapsedTeams[teamId],
      };
    },

    selectPlayer(player) {
      this.$emit("select-player", player);
    },

    imgUrl(pic) {
      if (!pic) return "";
      if (pic.startsWith("http") || pic.startsWith("/images/")) return pic;
      return `/images/${pic}`;
    },

    getInitials(name) {
      if (!name) return "?";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    getStatusStyle(player) {
      const status = Nutrition.getPlayerNutritionStatus(player);
      return {
        backgroundColor: status.bgColor,
        color: status.color,
      };
    },

    getStatusLabel(player) {
      const status = Nutrition.getPlayerNutritionStatus(player);
      return status.label;
    },
  },
};
</script>

<style scoped lang="scss">
.player-nutrition-list {
  padding: 16px;
}

.list-header {
  margin-bottom: 16px;
}

.search-box {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  background: #f8f9fa;

  &:focus {
    outline: none;
    border-color: #007bff;
    background: white;
  }
}

.filter-row {
  display: flex;
  gap: 12px;
}

.team-filter,
.status-filter {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.summary-card {
  background: white;
  border-radius: 12px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .card-value {
    display: block;
    font-size: 1.5rem;
    font-weight: 700;
    color: #333;
  }

  .card-label {
    font-size: 0.75rem;
    color: #666;
    text-transform: uppercase;
  }

  &.logged {
    .card-value {
      color: #28a745;
    }
  }

  &.attention {
    .card-value {
      color: #ffc107;
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #666;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e9ecef;
    border-top-color: #007bff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.team-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.team-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.team-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  border: none;
  border-radius: 10px;
  background: #f1f3f5;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: #e9ecef;
  }

  &:focus-visible {
    outline: 2px solid #007bff;
    outline-offset: 2px;
  }
}

.team-chevron {
  color: #666;
  flex-shrink: 0;
  transition: transform 0.2s ease;

  &.collapsed {
    transform: rotate(-90deg);
  }
}

.team-header-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #333;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-header-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.team-logged {
  font-size: 0.75rem;
  color: #666;
}

.team-count {
  min-width: 24px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #dee2e6;
  color: #495057;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
}

.player-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.player-card {
  display: grid;
  grid-template-columns: 50px 1fr auto auto 24px;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

.player-avatar {
  .avatar-img {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
  }

  .avatar-placeholder {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: 600;
    font-size: 1rem;
  }
}

.player-info {
  min-width: 0;

  .player-name {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .player-team {
    font-size: 0.8rem;
    color: #666;
  }
}

.nutrition-stats {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 100px;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.stat-icon {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;

  &.hydration {
    background: #17a2b8;
  }

  &.meals {
    background: #28a745;
  }
}

.stat-bar {
  flex: 1;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.stat-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;

  &.hydration {
    background: linear-gradient(90deg, #17a2b8, #20c997);
  }
}

.stat-value {
  font-size: 0.75rem;
  font-weight: 600;
  color: #666;
  min-width: 35px;
  text-align: right;
}

.meals-count {
  font-size: 0.75rem;
  color: #666;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.card-arrow {
  color: #ccc;
  display: flex;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #666;

  .empty-icon {
    margin-bottom: 16px;
    color: #ccc;
  }

  h3 {
    margin: 0 0 8px;
    font-size: 1.1rem;
    color: #333;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
    max-width: 280px;
    margin: 0 auto;
  }
}

/* Mobile adjustments */
@media screen and (max-width: 480px) {
  .player-card {
    grid-template-columns: 44px 1fr auto 20px;
    gap: 8px;
    padding: 10px;
  }

  .nutrition-stats {
    display: none;
  }

  .player-avatar {
    .avatar-img,
    .avatar-placeholder {
      width: 44px;
      height: 44px;
    }
  }

  .summary-cards {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  .summary-card {
    padding: 10px 8px;

    .card-value {
      font-size: 1.25rem;
    }

    .card-label {
      font-size: 0.65rem;
    }
  }
}
</style>
